import { useState } from "react";
import { motion } from "motion/react";
import { Button } from "@/components/_untitled/base/buttons/button";
import { cx } from "@/utils/cx";
import { PageContainer } from "@/components/layout/PageContainer";
import { useNavigate } from "react-router";
import { build } from "@/config/routes";
import { i18n, prefixes, type UILanguageCode, useTypedTranslation } from "@/locales";
import {
    accountAndSupport,
    bidding,
    legal,
    main,
    paymentsAndFees,
    selling,
    shippingAndDelivery,
    trustAndSafety
} from "@/locales/faq-content";

interface FAQBlockProps {
    isFirst?: boolean;
    QnA: {
        [key in Omit<UILanguageCode, 'fr'> as string]: {
            title: string;
            questions: { question: string; answer: string; }[];
        };
    };
}

const FAQBlock = ({ QnA, isFirst }: FAQBlockProps) => {
    const [openQuestions, setOpenQuestions] = useState(new Set(isFirst ? [0] : []));


    const currentLang = i18n.language as UILanguageCode;
    const { title, questions } = QnA[currentLang];
    const handleToggle = (index: number) => {
        if (openQuestions.has(index)) {
            openQuestions.delete(index);
        } else {
            openQuestions.add(index);
        }
        setOpenQuestions(new Set(openQuestions));
    };

    return <div className="mx-auto mt-12 max-w-3xl md:mt-16">
        <p className="text-4xl py-9 font-medium">{ title }</p>
        <div className="flex flex-col gap-8">
            { questions.map((faq, index) => (
                <div key={ faq.question }
                     className="not-first:-mt-px not-first:border-t not-first:border-secondary not-first:pt-6">
                    <h3>
                        <button
                            onClick={ () => handleToggle(index) }
                            className="flex w-full cursor-pointer items-start justify-between gap-2 rounded-md text-end outline-focus-ring select-none focus-visible:outline-2 focus-visible:outline-offset-2 md:gap-6"
                        >
                            <span className="text-md font-semibold text-primary">{ faq.question }</span>

                            <span aria-hidden="true"
                                  className="flex size-6 items-center text-fg-quaternary">
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <line
                                                    className={ cx(
                                                        "origin-center rotate-0 transition duration-150 ease-out",
                                                        openQuestions.has(index) && "-rotate-90",
                                                    ) }
                                                    x1="12"
                                                    y1="8"
                                                    x2="12"
                                                    y2="16"
                                                ></line>
                                                <line x1="8" y1="12" x2="16" y2="12"></line>
                                            </svg>
                                        </span>
                        </button>
                    </h3>

                    <motion.div
                        className="overflow-hidden"
                        initial={ false }
                        animate={ {
                            height: openQuestions.has(index) ? "auto" : 0,
                            opacity: openQuestions.has(index) ? 1 : 0,
                        } }
                        transition={ {
                            type: "spring",
                            damping: 24,
                            stiffness: 240,
                            bounce: 0.4,
                        } }
                    >
                        <div className="pt-1 pe-8 md:pe-12">
                            <p className="text-md text-tertiary">{ faq.answer }</p>
                        </div>
                    </motion.div>
                </div>
            )) }
        </div>
    </div>
}

export const FAQPage = () => {
    const navigate = useNavigate();
    const [tHeader, tFooter] = useTypedTranslation(prefixes('faq.header', 'faq.footer'));

    return (
        <PageContainer className="py-16 md:py-24">
            {/* Header */ }
            <div className="mx-auto flex w-full max-w-3xl flex-col items-center text-center">
                <h2 className="text-display-sm font-semibold text-primary md:text-display-md">
                    { tHeader('title') }
                </h2>
                <p className="mt-4 text-lg text-tertiary md:mt-5 md:text-xl">
                    { tHeader('description') }
                </p>
            </div>

            {/* FAQ Section */ }
            <FAQBlock QnA={ main } isFirst={ true }/>
            <FAQBlock QnA={ bidding }/>
            <FAQBlock QnA={ selling }/>
            <FAQBlock QnA={ paymentsAndFees }/>
            <FAQBlock QnA={ shippingAndDelivery }/>
            <FAQBlock QnA={ trustAndSafety }/>
            <FAQBlock QnA={ accountAndSupport }/>
            <FAQBlock QnA={ legal }/>

            {/* Footer */ }
            <div
                className="mt-12 flex flex-col items-center gap-6 rounded-2xl bg-secondary px-6 py-8 text-center md:mt-16 md:gap-8 md:pt-8 md:pb-10">
                <div>
                    <h4 className="text-xl font-semibold text-primary">{ tFooter('title') }</h4>
                    <p className="mt-2 text-md text-tertiary md:text-lg">
                        { tFooter('description') }
                    </p>
                </div>
                <Button size="xl" onClick={ () => navigate(build.pages('contact')) }>
                    { tFooter('button') }
                </Button>
            </div>
        </PageContainer>
    );
};
